import React, { useRef, useEffect, useLayoutEffect, useState } from 'react';
import ReactPlayer from 'react-player';
import { useSelector } from 'react-redux';
import {deviceDetect, isMobile, mobileModel} from "react-device-detect";
import { getNavbarHeight, getViewportDimensions } from '../../../../models/Utils';

import { ADALandmarks, ADAARIARoles } from '../../../../models/ADA';

import LearnMoreLink from '../../../Common/LearnMoreLink/LearnMoreLink';

import './TopSection.scss';

const mobileScreenThreshold = 767; // MUST BE IN SYNC WITH CSS

const TopSection = React.forwardRef(({ introData, sectionHeight, hideFromADA }, theRef) => {
  const [onMobile, setOnMobile] = useState(false);
  const menuOpened = useSelector(state => state.data.menuOpened);
  const cmsMediaPath = useSelector(state => state.config.basePath + state.config.apiMediaPath);
  const refScreenWidth = useRef(-1);
  const refViewportDims = useRef(null);
  const refVideoHolder = useRef(null);
  const [videoDimensions, setVideoDimensions] = useState({ width: 0, height: 0 });
  const [videoMuted, setVideoMuted] = useState(true);
  const [videoPlaying, setVideoPlaying] = useState(true);
  const [showingCaret, setShowingCaret] = useState(true);
  const errorHandlingAttempted = useRef(false);

  const originalDimensions = {
    desktop: { width: 1920, height: 360},
    mobile: { width: 750, height: 450}
  };

  const handleResize = () => {
    const viewport = getViewportDimensions();
    if (refScreenWidth.current !== viewport.width) {
      const isMobile = viewport.width <= mobileScreenThreshold;
      setOnMobile(isMobile);

      const targetDimensions = isMobile ? originalDimensions.mobile : originalDimensions.desktop;
      const scaleFactor = viewport.width / targetDimensions.width;
      const finalDimensions = {
        width: targetDimensions.width * scaleFactor,
        height: targetDimensions.height * scaleFactor
      };
      setVideoDimensions(finalDimensions);
      
      if (refVideoHolder.current) {
        const parent = refVideoHolder.current.parentNode;
        const left = -parent.getBoundingClientRect().left;
        refVideoHolder.current.style.left = `${left}px`;
      }

      refScreenWidth.current = viewport.width;
      refViewportDims.current = {
        navbarHeight: getNavbarHeight(),
        ...viewport
      };
    }
  };

  const handleScroll = () => {
    if (!refVideoHolder.current || !refViewportDims.current) return;

    const viewDims = refViewportDims.current;
    const portionOfVideoIn = 0.5;
    const videoRect = refVideoHolder.current.getBoundingClientRect();
    if (videoRect.width <= 0 || videoRect.height <= 0) return;
    if (
      (
        videoRect.top > viewDims.navbarHeight && videoRect.top < viewDims.height && videoRect.top + videoRect.height * portionOfVideoIn < viewDims.height
        ||
        videoRect.top + videoRect.height < viewDims.height && videoRect.top + videoRect.height > viewDims.navbarHeight && videoRect.top + videoRect.height - videoRect.height * portionOfVideoIn > viewDims.navbarHeight
      )
      ) {
      setVideoPlaying(true);
    } else {
      setVideoPlaying(false);
    }

    if (document.body.scrollTop === 0) {
      setShowingCaret(true);
    } else {
      setShowingCaret(false);
    }
  };

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener('resize', handleResize);
    document.body.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const loopVideo = () => {
    if (!videoPlaying) {
      setVideoPlaying(true);
    }
  };

  const setWaveformMute = (muted) => {
    setVideoMuted(muted);
  };

  useEffect( () => {
    console.log(deviceDetect());
  },[]);

  return (
    <div 
      className="credo-intro-container" 
      ref={theRef}
      style={{
        visibility: hideFromADA ? 'hidden' : null
      }}
    >
      <div className="cpvi-grid-container">
        <div className="cpvi-grid-row">
          <div className="cpvi-grid-col w12">
            <div className="credo-content-holder" style={{ height: `${sectionHeight}px`}}>
              <div className="credo-content-wrapper">
                <div className="credo-title-and-subtitle">
                  <div className="credo-main-title" role={ADAARIARoles.Heading} aria-label={introData.AltText}>
                    {onMobile && deviceDetect().os === 'iOS' && deviceDetect().model === 'iPhone' && introData.GifTitle !== null ?
                      <img style={{width:'320px', height:'86px'}} src={cmsMediaPath + introData.GifTitle} />
                      : 
                      <ReactPlayer
                      url={onMobile ? cmsMediaPath + introData.VideoTitleMobile :  cmsMediaPath + introData.VideoTitleDesktop}
                      width={onMobile ? 320 : deviceDetect().model === 'iPad' ? '100%' : 1100}
                      height={86}
                      muted={true}
                      loop={false}
                      playing={true}
                      volume={0}
                      config={{
                        youtube: introData.WaveformVideoMobile.indexOf("youtube.com") < 0 ? null : {
                          playerVars: {
                            autoplay: 1,
                            showinfo: 0
                          },
                          onUnstarted: () => loopVideo()
                        }
                      }}
                    />}
                  </div>
                  <div className="credo-main-subtitle cont-75">{introData.Subtitle}</div>
                </div>
                <LearnMoreLink
                  className="credo-button"
                  text={introData.CredoLink.Text}
                  external={true}
                  to={introData.CredoLink.Link}
                  specialAltText={introData.CredoLink.SpecialAltText}
                />
                <div className="credo-waveform">
                  <div className="credo-wf-holder">
                    <div
                      className="credo-wf-video-holder"
                      ref={refVideoHolder}
                      style={{
                        width: `${videoDimensions.width}px`,
                        height: `${videoDimensions.height}px`
                      }}
                      role={ADAARIARoles.Group}
                      tabIndex="-1"
                      aria-label={`Animation`}
                    >
                      <div
                        className="credo-wf-video-holder-inner"
                        role="img"
                        aria-label={introData.WaveformVideoADA}
                      >
                        
                        <ReactPlayer
                          url={onMobile ? deviceDetect().os === 'iOS' && deviceDetect().model === 'iPhone' ? introData.WaveformVideoMobileYouTube : cmsMediaPath + introData.WaveformVideoMobile : cmsMediaPath + introData.WaveformVideoDesktop}
                          //url={'https://www.youtube.com/embed/DnwkrvIFDUs'}
                          tabIndex={-1}
                          width={videoDimensions.width}
                          height={videoDimensions.height}
                          muted={videoMuted}
                          loop={true}
                          playing={!hideFromADA && videoPlaying}
                          volume={videoMuted ? 0 : 1}
                          onError={(err) => {
                            setVideoMuted(true);
                            setVideoPlaying(false);
                            if (!errorHandlingAttempted.current) {
                              setTimeout(() => {
                                errorHandlingAttempted.current = true;
                                setVideoPlaying(true);
                              }, 10);
                            }
                          }}
                          config={{
                            youtube: introData.WaveformVideoMobile.indexOf("youtube.com") < 0 ? null : {
                              playerVars: {
                                autoplay: 1,
                                showinfo: 0,
                                modestbranding: 1
                              },
                              onUnstarted: () => loopVideo()
                            }
                          }}
                          onReady={ () => loopVideo() }
                        />
                        <div
                          className='video-blocker' 
                          tabIndex={-1}
                          style={{
                          width:videoDimensions.width, 
                          height:videoDimensions.height, 
                          pointerEvents:'all', 
                          position:'absolute',
                          top:0
                          }}/>
                      </div>
                      <div className="credo-wf-button-holder">
                        <button
                          className={`credo-wf-mute-button ${videoMuted ? 'muted' : ''}`}
                          onClick={() => setWaveformMute(!videoMuted)}
                          aria-label="Toggle audio"
                          tabIndex={!menuOpened ? 0 : -1}
                        />
                        
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      { showingCaret && <div className="credo-intro-caret"/> }
    </div>
  );
});

export default TopSection;